import { MedicalId } from "../types";
import { DownloadApp } from "../components/DownloadApp";
import { NotificationBanner } from "../components/v4/notification-banner";
import { Lock,Eye } from "lucide-react";
import { useState,useMemo } from "react";
import { getMedicalIdFromUrl } from "../utils/get-medical-id-from-url";
import { LinkedResources } from "../components/v4/linked-resources";
import { ContactCard } from "../components/v4/contact-card";
import { CurrentLocation } from "../components/v4/current-location";
import { LocalEmergencyCallButton } from "../components/v4/local-emergency-button";
import { EmergencyServicesButton } from "../components/v4/emergency-service-button";
import { MedicalIdCard } from "../components/v4/medical-id-card/medical-id-card";
import { TwicemeTextLogo } from "../assets/illustrations/twiceme-text-logo";
import { SegmentedField } from "../components/v4/segmented-field";
import { fallbackString } from "../utils/fallback-string";
import { TwoSegmentField } from "../components/v4/two-segment-field";
import { ViewField } from "../components/v4/view-field";


export const MedicalIdDisplay = () => {
    const [showAppDownloadBanner, setShowAppDownloadBanner] = useState(true)
    
    const isUserURL = window.location.href.includes('/user')

    function isMedicalId(data: any): data is MedicalId {
        return (
          data && typeof data === 'object' && 'chronicConditions' in data // check a unique key!
        )
      }

      const medicalId = useMemo(() => {
        if (isUserURL) {
          return {} as MedicalId
        }
    
        return getMedicalIdFromUrl(window.location.href)
      }, [isUserURL])
    
      const onClick = () => {
        setShowAppDownloadBanner(false)
      }

    const isMedicalIdResult = isMedicalId(medicalId)

    function formatImperialHeightWithSpacing(value: string): string {
        return value
          .replace(/['´]/g, '′')
          .replace(/[“”"]/g, '″')
          .replace(/(\d+)′\s*(\d+)″/, '$1′ $2″')
      }
    if (isMedicalIdResult) {
        return (
          <div className="font-Inter bg-background h-[100vh] p-3 gap-4 flex flex-col pt-10 pb-10 overflow-y-auto">
            <DownloadApp visible={showAppDownloadBanner} onClick={onClick} />
            <NotificationBanner
              icon={<Lock />}
              content={
                <p>
                  Please note that this information is read on smartphone only and
                  not uploaded to web.{' '}
                  <a
                    className="text-primary-black underline"
                    href="https://www.twiceme.com/support#1683790397871-5884dd8c-db6f"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read more
                  </a>
                </p>
              }
            />
            <div>
              <TwicemeTextLogo />
            </div>
            <div className="flex flex-col gap-3">
              <MedicalIdCard user={medicalId} />
            </div>
            <div className="flex flex-col gap-2">
              <EmergencyServicesButton />
              <LocalEmergencyCallButton />
            </div>
            <CurrentLocation />
            <div className="flex flex-col gap-2">
              {medicalId.emergencyContacts.length > 0 && medicalId.emergencyContacts.map((contact) => (
                <div className="flex flex-col gap-3">
                  <h2 className="text-2xl font-bold">Emergency contacts</h2>
                  <ContactCard contact={contact} />
                </div>
              ))}
            </div>
            {medicalId?.linkedResources && medicalId.linkedResources.length > 0 && (
              <div className="flex flex-col gap-3">
                <h2 className="text-2xl font-bold">Linked resources</h2>
                <LinkedResources resources={medicalId.linkedResources} />
              </div>
            )}
            <div className="flex flex-col gap-3">
              <h2 className="text-2xl font-bold">Medical information</h2>
              <div className="bg-primary-white rounded-2xl p-3">
                <p>
                  {fallbackString(
                    medicalId.chronicConditions,
                    'No information added'
                  )}
                </p>
              </div>
              <SegmentedField label="Pacemaker" status={medicalId.pacemaker} />
              <SegmentedField label="Organ donor" status={medicalId.organDonor} />
            </div>
            <div className="flex flex-col gap-3">
              <h2 className="text-2xl font-bold">Identification characteristics</h2>
              <div className="bg-primary-white rounded-2xl p-3">
                <p>
                  {fallbackString(
                    medicalId.identificationCharacteristics,
                    'No information added'
                  )}
                </p>
              </div>
              {medicalId.height && medicalId.weight && (
                <div className="flex flex-row w-full gap-2">
                  <TwoSegmentField
                    label="HT"
                    value={
                      medicalId.systemOfMeasurement === 'metric'
                        ? `${medicalId.height} cm`
                        : formatImperialHeightWithSpacing(medicalId.height)
                    }
                    className="flex-1"
                  />
                  <TwoSegmentField
                    label="WT"
                    value={
                      medicalId.systemOfMeasurement === 'metric'
                        ? `${medicalId.weight} kg`
                        : `${medicalId.weight} lbs`
                    }
                    className="flex-1"
                  />
                </div>
              )}
              <ViewField
                label="Eye color"
                value={fallbackString(medicalId.eyeColor)}
                icon={Eye}
              />
            </div>
            <div className="flex flex-col gap-3">
              <h2 className="text-2xl font-bold">Insurance, ID and Passports</h2>
              <ViewField
                label="Insurance company name"
                value={fallbackString(medicalId.insuranceCompanyName)}
              />
              <ViewField
                label="Insurance number"
                value={fallbackString(medicalId.insuranceNumber)}
              />
              <ViewField
                label="Phone number"
                value={fallbackString(medicalId.insurancePhoneNumber)}
              />
              <ViewField
                label="ID/SSN number"
                value={fallbackString(medicalId.ssn)}
              />
              <ViewField
                label="Passport number"
                value={fallbackString(medicalId.passportNumber)}
              />
            </div>
          </div>
        )
      }
    
      return (
        <div className="flex flex-col gap-3 p-3 justify-center items-center">
          <p className="text-center">No medical id was provided.</p>
          <p className="text-center text-sm text-gray-500 break-all">
            {JSON.stringify(medicalId)}
          </p>
          <p className="text-sm text-gray-500 break-all">{window.location.href}</p>
        </div>
      )
}